<script setup>
import { computed, useId, useTemplateRef } from "vue";

import FormField from "./FormField.vue";
import SvgIcon from "./SvgIcon.vue";

defineOptions({ inheritAttrs: false });
const props = defineProps({
  modelValue: { type: [String, Number], default: "" },
  label: { type: String, default: "" },
  info: { type: String, default: "" },
  placeholder: { type: String, default: "" },
  type: { type: String, default: "text" },
  disabled: { type: Boolean, default: false },
  error: { type: String, default: "" },
  inlineError: { type: Boolean, default: false },
  invalid: { type: Boolean, default: false },
  id: { type: String, default: "" },
  prefix: { type: String, default: "" },
  loading: { type: Boolean, default: false },
});
defineEmits(["update:modelValue"]);

const generatedId = useId();
const resolvedId = computed(() => props.id || generatedId);
const inputRef = useTemplateRef("input");

function focus() {
  inputRef.value.focus();
}

function checkValidity() {
  return inputRef.value.checkValidity();
}

function setCustomValidity(message) {
  inputRef.value.setCustomValidity(message);
}

defineExpose({ focus, checkValidity, setCustomValidity, input: inputRef });
</script>

<template>
  <FormField :id="resolvedId" :label :info :error :inline-error>
    <template v-if="$slots['label-info']" #label-info="{ id: descriptionId }">
      <slot :id="descriptionId" name="label-info" />
    </template>
    <template #default="{ describedBy }">
      <div class="input-wrap" :class="{ error: error || invalid }">
        <slot name="icon-left" />
        <span v-if="prefix" class="input-prefix">{{ prefix }}</span>
        <SvgIcon v-if="loading" name="system/loader" width="14" height="14" class="spinner" />
        <input
          :id="resolvedId"
          ref="input"
          :type
          :value="modelValue"
          :placeholder
          :disabled
          :aria-invalid="!!error || invalid"
          :aria-describedby="describedBy"
          class="input"
          :class="{
            'has-icon-left': $slots['icon-left'],
            'has-icon-right': $slots['icon-right'],
            'has-prefix': prefix,
          }"
          v-bind="$attrs"
          @input="
            $emit(
              'update:modelValue',
              type === 'number' && $event.target.value !== ''
                ? Number($event.target.value)
                : $event.target.value,
            )
          "
        />
        <span v-if="$slots['icon-right']" class="icon-right">
          <slot name="icon-right" />
        </span>
      </div>
    </template>
  </FormField>
</template>

<style lang="scss" scoped>
@use "@/colors";

.input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.input {
  width: 100%;
  height: 36px;
  padding: 0 0.75rem;
  font-family: inherit;
  font-size: 0.8125rem;
  color: colors.$text-color;
  background-color: colors.$white;
  border: 1px solid colors.$border-light-color;
  border-radius: 6px;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: colors.$grey-neutral-50;
  }

  &:hover:not(:focus, :disabled) {
    border-color: colors.$grey-neutral-50;
  }

  &:focus {
    border-color: colors.$main;
  }

  &:disabled {
    background-color: colors.$grey-lightest;
    cursor: not-allowed;
  }

  &.has-icon-left {
    padding-left: 2.25rem;
  }

  &.has-icon-right {
    padding-right: 2.5rem;
  }

  &.has-prefix {
    padding-left: 3rem;
  }
}

.icon-right {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.input-prefix {
  position: absolute;
  left: 0.75rem;
  color: colors.$grey-neutral-50;
  font-size: 0.8125rem;
  pointer-events: none;
}

.spinner {
  position: absolute;
  right: 0.5rem;
  pointer-events: none;
  animation: spin 0.8s linear infinite;
  color: colors.$grey-neutral-50;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.input-wrap.error .input {
  border-color: colors.$brand-danger;
}
</style>
